<script setup lang="ts">
import {
  Download as DownloadIcon,
  Edit as EditIcon,
  Eye as EyeIcon,
  EyeOff as EyeOffIcon,
  Loader2 as Loader2Icon,
  Route as RouteIcon,
  Trash2 as Trash2Icon,
} from '@lucide/vue'

import type { Track } from '@/types/tour'

withDefaults(
  defineProps<{
    track: Track
    isVisible: boolean
    canRouteEdit: boolean
    canEditTrack?: boolean
    canDeleteTrack?: boolean
    isEditing: boolean
    isDownloading: boolean
    routeEditTitle: string
  }>(),
  {
    canEditTrack: true,
    canDeleteTrack: true,
  },
)

const emit = defineEmits<{
  toggleVisibility: []
  edit: []
  routeEdit: []
  download: []
  delete: []
}>()
</script>

<template>
  <div class="flex items-center gap-0.5">
    <button
      v-if="isVisible"
      class="p-1.5 text-gray-500 hover:text-gray-700 hover:cursor-pointer"
      title="Hide track"
      @click.stop="emit('toggleVisibility')"
    >
      <EyeIcon class="w-3.5 h-3.5" />
    </button>
    <button
      v-else
      class="p-1.5 text-gray-500 hover:text-gray-700 hover:cursor-pointer"
      title="Show track"
      @click.stop="emit('toggleVisibility')"
    >
      <EyeOffIcon class="w-3.5 h-3.5" />
    </button>
    <button
      v-if="canEditTrack"
      class="p-1.5 text-primary-500 hover:text-primary-700 hover:cursor-pointer"
      title="Edit track"
      @click.stop="emit('edit')"
    >
      <EditIcon class="w-3.5 h-3.5" />
    </button>
    <button
      v-if="canRouteEdit && canEditTrack"
      :disabled="isEditing"
      :class="
        isEditing
          ? 'p-1.5 text-amber-700 bg-amber-50 rounded cursor-default'
          : 'p-1.5 text-amber-500 hover:text-amber-700 hover:cursor-pointer'
      "
      :title="routeEditTitle"
      @click.stop="emit('routeEdit')"
    >
      <RouteIcon class="w-3.5 h-3.5" />
    </button>
    <button
      class="p-1.5 text-green-500 hover:text-green-700 hover:cursor-pointer"
      title="Download GPX"
      :disabled="isDownloading || !track.clientId"
      @click.stop="emit('download')"
    >
      <component
        :is="isDownloading ? Loader2Icon : DownloadIcon"
        class="w-3.5 h-3.5"
        :class="{ 'animate-spin': isDownloading }"
      />
    </button>
    <button
      v-if="canDeleteTrack"
      class="p-1.5 text-red-500 hover:text-red-700 hover:cursor-pointer"
      title="Delete track"
      @click.stop="emit('delete')"
    >
      <Trash2Icon class="w-3.5 h-3.5" />
    </button>
  </div>
</template>
